import { logger } from '~/utils/loggerUtil';
import { getGAParams } from '~/utils/measurementUtils';

export enum LocalStorageKeys {
  referrer = 'cw-v5-ref',
  utmContent = 'cw-v5-ucnt',
  utmSource = 'cw-v5-usrc',
  utmMedium = 'cw-v5-umed',
  utmCampaign = 'cw-v5-ucam',
  landingPage = 'cw-v5-land',
  overseasFlightData = 'cw-v1-ofd',
  notificationData = 'cw-notifications',
  attributionUtmValues = 'cw-attribution-utm-values',
  orderConfirmRevisitInfoV1 = 'ORDER_CONFIRM_REVISIT_INFO_V1',
}

export class LocalStorage {
  static data: {
    [k in keyof LocalStorageKeys]: string;
  };

  static get isSameDomainReferrer(): boolean {
    return /^http(s)?:\/\/(www\.)?(cocolocala\.jp|staging-cocolocala\.work|localhost:3000|localhost:3001|localhost:3002|localhost:11029).*/.test(
      document.referrer
    );
  }

  static removeData(key: LocalStorageKeys): void {
    localStorage.removeItem(key);
  }

  static getData(key: LocalStorageKeys): string | undefined {
    return localStorage.getItem(key) ?? undefined;
  }

  static setData(key: LocalStorageKeys, data: string): void {
    localStorage.setItem(key, data);
  }

  static setReferrer(): void {
    this.setData(LocalStorageKeys.referrer, document.referrer);
  }

  static setLandingPage(): void {
    this.setData(
      LocalStorageKeys.landingPage,
      window.location.origin + window.location.pathname
    );
  }

  static setGAParams(): void {
    const gaParams = getGAParams();
    if (typeof gaParams === 'undefined') {
      return;
    }

    localStorage.setItem(LocalStorageKeys.utmContent, gaParams.utmContent);
    localStorage.setItem(LocalStorageKeys.utmSource, gaParams.utmSource);
    localStorage.setItem(LocalStorageKeys.utmMedium, gaParams.utmMedium);
    localStorage.setItem(LocalStorageKeys.utmCampaign, gaParams.utmCampaign);
  }
}

/**
 * ローカルストレージからGAのパラメータを取得
 */
export const getGAParamsFromLocalStorage = () => {
  return {
    utmContent: localStorage.getItem(LocalStorageKeys.utmContent) ?? '',
    utmSource: localStorage.getItem(LocalStorageKeys.utmSource) ?? '',
    utmMedium: localStorage.getItem(LocalStorageKeys.utmMedium) ?? '',
    utmCampaign: localStorage.getItem(LocalStorageKeys.utmCampaign) ?? '',
  };
};

export type Notification = {
  title?: string;
  message?: string;
};
/**
 * ローカルストレージに通知のデータを追加
 * @param notification
 */
export const addNotificationToLocalStorage = (notification: Notification) => {
  const newNotifications = getNotificationFromLocalStorage().slice();
  newNotifications.push(notification);
  try {
    const json = JSON.stringify(newNotifications);
    LocalStorage.setData(LocalStorageKeys.notificationData, json);
  } catch (error) {
    logger.error(error);
  }
};

/**
 * ローカルストレージに通知のデータを取得
 */
export const getNotificationFromLocalStorage = (): Notification[] => {
  const current = LocalStorage.getData(LocalStorageKeys.notificationData);
  if (typeof current === 'undefined' || current === '') {
    return [];
  }
  try {
    const data = JSON.parse(current);
    if (Array.isArray(data)) {
      return data
        .filter(
          item =>
            typeof item.title !== 'undefined' ||
            typeof item.message !== 'undefined'
        )
        .map(item => item as Notification);
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * ローカルストレージから通知を削除する
 */
export const clearNotificationsToLocalStorage = () => {
  LocalStorage.setData(LocalStorageKeys.notificationData, JSON.stringify([]));
};
